import { css } from "@emotion/react";
import { theme } from "../../styles/theme/theme";
import { CHECKBOX_COL_W, MARKED_READY_COL_W, RETAILER_COL_W, STATUS_COL_W, SKU_COL_W } from "./constants";

export const s = {
  page: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${theme.colors.background};
  `,
  /** Quiet line directly under the header that hosts the run selector. */
  runSelectorRow: css`
    flex-shrink: 0;
    padding: 0.375rem 0.75rem;
    border-bottom: 1px solid ${theme.colors.border};
  `,
  applyEditsToggle: css`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 11px;
    color: ${theme.colors.mutedForeground};
    user-select: none;
    margin-left: auto;
    margin-bottom: 0.5rem;
  `,
  engineViewBanner: css`
    margin: 0;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid ${theme.colors.border};
    background: ${theme.colors.blue50};
    font-size: 0.75rem;
    color: ${theme.colors.gray700};
  `,
  /** Positioning context for the refresh overlay; takes the flex slot the
   *  content div used to own so the layout is unchanged when not refreshing. */
  contentWrap: css`
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  `,
  content: css`
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex: 1;
    min-height: 0;
    overflow: auto;
  `,
  /** Small non-blocking "saving/updating" pill in the corner of the grid during
   *  a re-fetch (CHO-693). Deliberately does NOT cover or block the table — the
   *  grid stays fully visible (showing optimistic edited values) and the planner
   *  keeps their place. pointer-events: none so it never intercepts clicks. */
  refreshPill: css`
    position: absolute;
    top: 0.6rem;
    right: 1rem;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.3rem 0.65rem;
    border-radius: 999px;
    background: color-mix(in srgb, ${theme.colors.gray900} 82%, transparent);
    color: ${theme.colors.background};
    font-size: 0.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    pointer-events: none;
  `,
  refreshPillSpinner: css`
    width: 0.75rem;
    height: 0.75rem;
    border: 2px solid color-mix(in srgb, ${theme.colors.background} 40%, transparent);
    border-top-color: ${theme.colors.background};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
  `,
  panelCard: css`
    border: 1px solid ${theme.colors.border};
    border-radius: ${theme.borderRadius.lg};
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: ${theme.colors.background};
  `,
  topPanel: css`
    height: 300px;
    flex-shrink: 0;
  `,
  topPanelCollapsed: css`
    flex-shrink: 0;
    height: auto;
    overflow: visible;
  `,
  bottomPanel: css`
    flex: 1;
    min-height: 500px;
  `,
  panelHeader: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.625rem 0.75rem;
    border-bottom: 1px solid ${theme.colors.border};
    background: ${theme.colors.background};
    flex-shrink: 0;
    min-height: 2.75rem;
  `,
  panelHeaderLeft: css`
    display: flex;
    align-items: center;
  `,
  panelHeaderLeftGap: css`
    display: flex;
    align-items: center;
    gap: 0.75rem;
  `,
  panelTitle: css`
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${theme.colors.foreground};
  `,
  panelDesc: css`
    font-size: 0.75rem;
    color: ${theme.colors.mutedForeground};
    margin-left: 0.75rem;
  `,
  panelDescNoMargin: css`
    font-size: 0.75rem;
    color: ${theme.colors.mutedForeground};
  `,
  drilldownBadge: css`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.625rem;
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.blue50};
    color: ${theme.colors.blue700};
    font-size: 0.75rem;
    font-weight: 500;
  `,
  drilldownClear: css`
    font-size: 0.6875rem;
    color: ${theme.colors.mutedForeground};
    cursor: pointer;
    text-decoration: underline;
    &:hover {
      color: ${theme.colors.foreground};
    }
  `,
  editInput: css`
    position: absolute;
    inset: 0;
    width: 100%;
    border: none;
    border-right: 1px solid ${theme.colors.gray200};
    border-radius: 0;
    padding: 0.375rem 0.5rem;
    font-size: 0.8125rem;
    font-variant-numeric: tabular-nums;
    text-align: right;
    outline: none;
    background: ${theme.colors.gray50};
    color: ${theme.colors.foreground};
    &:focus {
      background: ${theme.colors.white};
      box-shadow: inset 0 0 0 2px ${theme.colors.blue500};
    }
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  `,
  exportBtn: css`
    gap: 0.25rem;
    font-size: 0.75rem;
    color: ${theme.colors.mutedForeground};
    padding: 0.25rem 0.5rem;
    height: auto;
    &:hover {
      color: ${theme.colors.foreground};
    }
  `,
  // ── Checkbox column ──────────────────────────────────────────────────────
  checkboxCell: css`
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: ${CHECKBOX_COL_W}px;
    max-width: ${CHECKBOX_COL_W}px;
    background: ${theme.colors.background};
    text-align: center;
    padding: 0.5rem 0;
    vertical-align: middle;
  `,
  checkboxHeaderCell: css`
    position: sticky;
    left: 0;
    z-index: 3;
    min-width: ${CHECKBOX_COL_W}px;
    max-width: ${CHECKBOX_COL_W}px;
    background: ${theme.colors.muted};
    text-align: center;
    padding: 0.5rem 0;
    vertical-align: middle;
  `,
  checkboxInput: css`
    cursor: pointer;
    width: 14px;
    height: 14px;
    margin: 0;
    vertical-align: middle;
    accent-color: ${theme.colors.blue600};
  `,

  // ── Marked Ready date column (top table, position 4 / last frozen) ───────
  markedReadyCell: css`
    position: sticky;
    left: calc(${CHECKBOX_COL_W + STATUS_COL_W}px + var(--sku-col-w, ${SKU_COL_W}px));
    z-index: 1;
    min-width: var(--marked-ready-col-w, ${MARKED_READY_COL_W}px);
    max-width: var(--marked-ready-col-w, ${MARKED_READY_COL_W}px);
    background: ${theme.colors.background};
    padding: 0.5rem 0.75rem;
    color: ${theme.colors.mutedForeground};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.08);
  `,
  markedReadyHeaderCell: css`
    position: sticky;
    left: calc(${CHECKBOX_COL_W + STATUS_COL_W}px + var(--sku-col-w, ${SKU_COL_W}px));
    z-index: 3;
    min-width: var(--marked-ready-col-w, ${MARKED_READY_COL_W}px);
    max-width: var(--marked-ready-col-w, ${MARKED_READY_COL_W}px);
    background: ${theme.colors.muted};
    box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.08);
  `,

  // ── Status dots ──────────────────────────────────────────────────────────
  statusCell: css`
    position: sticky;
    left: ${CHECKBOX_COL_W}px;
    z-index: 1;
    min-width: ${STATUS_COL_W}px;
    max-width: ${STATUS_COL_W}px;
    background: ${theme.colors.background};
    text-align: center;
    padding: 0.5rem 0;
    cursor: pointer;
  `,
  topStatusCell: css`
    position: sticky;
    left: ${CHECKBOX_COL_W}px;
    z-index: 1;
    min-width: ${STATUS_COL_W}px;
    max-width: ${STATUS_COL_W}px;
    background: ${theme.colors.background};
    text-align: center;
    padding: 0.5rem 0;
  `,
  topStatusHeaderCell: css`
    position: sticky;
    left: ${CHECKBOX_COL_W}px;
    z-index: 3;
    min-width: ${STATUS_COL_W}px;
    max-width: ${STATUS_COL_W}px;
    background: ${theme.colors.muted};
    text-align: center;
  `,
  markReadyBtn: css`
    gap: 0.375rem;
    font-size: 0.75rem;
    padding: 0.25rem 0.75rem;
    height: auto;
    background: ${theme.colors.blue600};
    &:hover { background: ${theme.colors.blue700}; }
  `,

  // ── Top table first-receiver label cell ─────────────────────────────────
  topFirstReceiverCell: css`
    position: sticky;
    left: ${CHECKBOX_COL_W + STATUS_COL_W}px;
    z-index: 1;
    min-width: var(--sku-col-w, ${SKU_COL_W}px);
    max-width: var(--sku-col-w, ${SKU_COL_W}px);
    background: ${theme.colors.background};
    padding: 0.5rem 0.75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${theme.colors.foreground};
    font-weight: 400;
    cursor: pointer;
  `,

  statusDot: (color: string) => css`
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${color};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.1s;
    &:hover {
      transform: scale(1.3);
    }
  `,
  statusDotSmall: (color: string) => css`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${color};
    display: inline-block;
  `,
  statusLegend: css`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  `,
  statusLegendItem: css`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.6875rem;
    color: ${theme.colors.mutedForeground};
  `,

  // ── Save button ─────────────────────────────────────────────────────────
  saveBtn: css`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.875rem;
    border-radius: ${theme.borderRadius.md};
    border: none;
    background: ${theme.colors.blue600};
    color: white;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
    &:hover {
      background: ${theme.colors.blue700};
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `,
  savedLabel: css`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: ${theme.colors.itemStatus.sent};
    font-weight: 500;
  `,

  // ── Edited cell highlight ───────────────────────────────────────────────
  editInputEdited: css`
    position: absolute;
    inset: 0;
    width: 100%;
    border: none;
    border-right: 1px solid ${theme.colors.gray200};
    border-radius: 0;
    padding: 0.375rem 0.5rem;
    font-size: 0.8125rem;
    font-variant-numeric: tabular-nums;
    text-align: right;
    outline: none;
    background: ${theme.colors.blue50};
    color: ${theme.colors.foreground};
    &:focus {
      box-shadow: inset 0 0 0 2px ${theme.colors.blue500};
    }
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  `,

  // ── Retailer panel cells ─────────────────────────────────────────────────
  retailerStickyCell: css`
    position: sticky;
    left: ${STATUS_COL_W}px;
    z-index: 1;
    min-width: ${RETAILER_COL_W}px;
    max-width: ${RETAILER_COL_W}px;
    background: ${theme.colors.background};
    padding: 0.5rem 0.75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${theme.colors.foreground};
    font-weight: 400;
    box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.08);
    cursor: pointer;
  `,
  retailerNameRow: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `,
  retailerSkuCount: css`
    font-size: 0.6875rem;
    color: ${theme.colors.mutedForeground};
    font-weight: 400;
  `,
  retailerAvgCell: css`
    padding: 0.5rem 0.75rem;
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-weight: 400;
    cursor: pointer;
  `,

  // ── SKU panel cells ──────────────────────────────────────────────────────
  // Bottom-table column order is [SKU, planning_level]. The two sticky cols
  // share their width vars (--sku-col-w, --marked-ready-col-w) with the top
  // table's col 3 (first_receiver) and col 4 (marked_ready) so the fixed
  // region lines up vertically across both tables. tableWidthsStyle sizes
  // each var to fit content from both tables at that column index.
  skuNameCell: (isExpanded: boolean) => css`
    position: sticky;
    left: ${CHECKBOX_COL_W + STATUS_COL_W}px;
    z-index: 1;
    min-width: var(--sku-col-w, ${SKU_COL_W}px);
    max-width: var(--sku-col-w, ${SKU_COL_W}px);
    background: ${isExpanded ? theme.colors.gray200 : theme.colors.background};
    padding: 0.5rem 0.75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${theme.colors.foreground};
    font-weight: 400;
    cursor: pointer;
  `,
  skuPlanningLevelCell: (isExpanded: boolean) => css`
    position: sticky;
    left: calc(${CHECKBOX_COL_W + STATUS_COL_W}px + var(--sku-col-w, ${SKU_COL_W}px));
    z-index: 1;
    min-width: var(--marked-ready-col-w, ${MARKED_READY_COL_W}px);
    max-width: var(--marked-ready-col-w, ${MARKED_READY_COL_W}px);
    background: ${isExpanded ? theme.colors.gray200 : theme.colors.background};
    padding: 0.5rem 0.75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${theme.colors.mutedForeground};
    box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.08);
    cursor: pointer;
  `,
  skuExpandRow: css`
    display: flex;
    align-items: center;
    gap: 0.375rem;
  `,
  skuChevron: css`
    width: 0.875rem;
    height: 0.875rem;
    flex-shrink: 0;
    color: ${theme.colors.mutedForeground};
  `,
  skuValueCell: css`
    width: 5rem;
    min-width: 5rem;
    max-width: 5rem;
    padding: 0.5rem 0.75rem;
    text-align: right;
    font-variant-numeric: tabular-nums;
    cursor: pointer;
  `,
  skuTotalCell: css`
    padding: 0.5rem 0.75rem;
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-weight: 400;
    color: ${theme.colors.foreground};
    cursor: pointer;
  `,

  // ── Bottom table expand chevron (aligns with top table checkbox column) ─
  skuExpandCell: css`
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: ${CHECKBOX_COL_W}px;
    max-width: ${CHECKBOX_COL_W}px;
    background: ${theme.colors.background};
    text-align: center;
    padding: 0.5rem 0;
    cursor: pointer;
  `,

  // ── Detail row cells ─────────────────────────────────────────────────────
  detailSpacerCell0: css`
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: ${CHECKBOX_COL_W}px;
    max-width: ${CHECKBOX_COL_W}px;
    background: ${theme.colors.background};
    padding: 0;
  `,
  detailSpacerCell1: css`
    position: sticky;
    left: ${CHECKBOX_COL_W}px;
    z-index: 1;
    min-width: ${STATUS_COL_W}px;
    max-width: ${STATUS_COL_W}px;
    background: ${theme.colors.background};
    padding: 0;
  `,
  // Detail rows mirror the bottom-table column order — col 3 is the SKU
  // spacer (--sku-col-w) and col 4 holds the measure label, sized to the
  // same width as the planning_level cell above (--marked-ready-col-w).
  detailSpacerCell2: css`
    position: sticky;
    left: ${CHECKBOX_COL_W + STATUS_COL_W}px;
    z-index: 1;
    min-width: var(--sku-col-w, ${SKU_COL_W}px);
    max-width: var(--sku-col-w, ${SKU_COL_W}px);
    background: ${theme.colors.background};
    padding: 0;
  `,
  detailLabelCell: (isEnding: boolean) => css`
    position: sticky;
    left: calc(${CHECKBOX_COL_W + STATUS_COL_W}px + var(--sku-col-w, ${SKU_COL_W}px));
    z-index: 1;
    background: ${isEnding ? theme.colors.gray50 : theme.colors.background};
    padding: 0.375rem 0.75rem 0.375rem 0.75rem;
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: var(--marked-ready-col-w, ${MARKED_READY_COL_W}px);
    max-width: var(--marked-ready-col-w, ${MARKED_READY_COL_W}px);
    box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.08);
    color: ${theme.colors.foreground};
    font-weight: 400;
    font-size: 0.8125rem;
  `,
  detailEditableTag: css`
    margin-left: 0.25rem;
    font-size: 0.8125rem;
    color: ${theme.colors.blue500};
    font-weight: 400;
  `,
  // "(N)" chip shown in the planning_level cell on the collapsed SKU header
  // row, where N is the SKU's planning-level count. Pill background lifts it
  // out of the muted-gray default cell text so it reads as a discrete badge.
  skuHeaderPlCountBadge: css`
    display: inline-block;
    padding: 0.0625rem 0.375rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: ${theme.colors.mutedForeground};
    background: ${theme.colors.muted};
    border-radius: 999px;
    vertical-align: middle;
  `,
  skuHeaderName: css`
    font-weight: 500;
    color: ${theme.colors.foreground};
  `,
  iohSourceInfoIcon: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 0.375rem;
    color: ${theme.colors.mutedForeground};
    cursor: help;
    &:hover {
      color: ${theme.colors.foreground};
    }
  `,
  detailValueCell: css`
    width: 5rem;
    min-width: 5rem;
    max-width: 5rem;
    padding: 0.375rem 0.75rem;
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-size: 0.8125rem;
  `,
  detailEditCell: (isPast: boolean) => css`
    width: 5rem;
    min-width: 5rem;
    max-width: 5rem;
    padding: 0.375rem 0.75rem;
    text-align: right;
    position: relative;
    /* Confine the absolutely-positioned clear-override button (z-index: 1)
       to this cell's own stacking context. Without this, when the table is
       horizontally scrolled the clear button leaks through the sticky left
       columns (which also use z-index: 1) and appears as stray X icons
       floating over Retailer / SKU / metric-label cells. */
    isolation: isolate;
    ${isPast ? `background-color: ${theme.colors.pastPeriodBg};` : ""}
  `,
  detailPastValue: (hasValue: boolean) => css`
    font-variant-numeric: tabular-nums;
    font-size: 0.8125rem;
    color: ${hasValue ? theme.colors.green500 : theme.colors.mutedForeground};
  `,
  detailTotalCell: css`
    padding: 0.375rem 0.75rem;
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-size: 0.8125rem;
  `,
};
